'use client';

import React, { useMemo } from 'react';

import { getBatchImageSources } from '@/lib/blockAssets';
import { ImageSource } from '@/lib/images/cmsImage';
import { IBlock } from '@/lib/types';

import { Block } from './Block';
import { BlockBatchSkeleton } from './BlockBatchSkeleton';
import { useBatchAssetsReady } from './hooks/useBatchAssetsReady';

interface IBlockBatchProps {
  blocks: IBlock[];
  firstBlockIndex: number;
  /**
   * Render the blocks at once instead of behind the asset gate. For the batch
   * that ships in the document: its markup is what the browser paints first,
   * and its images are already requested from the HTML itself.
   */
  skipAssetGate?: boolean;
  /** Page index of the block whose first card is fetched eagerly (see findPrimaryPostBlockIndex). */
  primaryPostBlockIndex?: number;
}

export const BlockBatch: React.FC<IBlockBatchProps> = ({
  blocks,
  firstBlockIndex,
  skipAssetGate = false,
  primaryPostBlockIndex = -1,
}) => {
  const assetSources = useMemo(
    (): ImageSource[] => (skipAssetGate ? [] : getBatchImageSources(blocks)),
    [blocks, skipAssetGate]
  );
  const isReady = useBatchAssetsReady(assetSources);

  if (!isReady) {
    return <BlockBatchSkeleton blocks={blocks} firstBlockIndex={firstBlockIndex} />;
  }

  return (
    <>
      {blocks.map((block: IBlock, index: number) => (
        <Block
          key={`${block.type}-${firstBlockIndex + index}`}
          block={block}
          blockIndex={firstBlockIndex + index}
          isPrimaryPostBlock={firstBlockIndex + index === primaryPostBlockIndex}
          isBelowPrimaryPostBlock={
            primaryPostBlockIndex >= 0 && firstBlockIndex + index > primaryPostBlockIndex
          }
        />
      ))}
    </>
  );
};
